import React, { useRef, useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import BuyBox from '@src/components/ProductInfo/BuyBox'
import SignInBanner from '@src/components/SignInBanner'
import {
  DiscontinuedMaterialPricingDetailFragment,
  ValidMaterialPricingDetailFragment,
} from '@src/fragments/ProductPricing.generated'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { ViewPreference } from '@src/routes/SearchResults/SearchResults'
import MobileHeader from './MobileHeader'
import MobileProductDisplay from './MobileProductDisplay'
import NonModalSheet from '@src/components/NonModalSheet/NonModalSheet'
import ProductPriceAvailabilityError from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityError'
import { getSellerName } from '@src/utils/searchUtils'
import DiscontinuedProductAlert from '@src/components/DiscontinuedProductAlert'
import ProductPaMessage, {
  ProductPaMessageDisplayType,
} from '@src/components/ProductPriceAvailability/ProductPaMessage'
import { EventActionComponentType } from '@src/utils/analytics/enums'

const SELECTED_OFFSET = 8

const useStyles = makeStyles()((theme) => ({
  mobileSignInBanner: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(6),
    },
    textAlign: 'center',
  },
  sheetContainer: {
    backgroundColor: theme.palette.background.grey,
  },
  sheetContentContainer: {
    maxWidth: 568,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
  },
  sheetContent: {
    padding: theme.spacing(0),
    '&.buyBoxContainer': {
      boxShadow: 'none',
    },
  },
  pricingError: {
    marginTop: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
  },
}))

interface SelectedMaterialPosition {
  height: number
  top: number
}

interface ViewProductOptionsMobileViewProps {
  materialPricing: ValidMaterialPricingDetailFragment[] | undefined
  pricingError: any
  pricingLoading: boolean
  view: ViewPreference | null
  handleSelectView: (view: ViewPreference) => void
  showAllOptions: boolean
  setShowAllOptions: (show: boolean) => void
  isBuyBoxSheetOpen: boolean
  setIsBuyBoxSheetOpen: (open: boolean) => void
  product: PdpFieldsFragment & {
    brandKey: string
  }
  countryCode: string
  isMultiCountryUser: boolean
  mobileSelectedMaterial: ValidMaterialPricingDetailFragment | null
  setMobileSelectedMaterial: (
    material: ValidMaterialPricingDetailFragment | null
  ) => void
  handleCloseBuyBoxSheet: () => void
  discontinuedPricingInfo:
    | DiscontinuedMaterialPricingDetailFragment
    | undefined
    | null
}

const ViewProductOptionsMobileView: React.FC<
  ViewProductOptionsMobileViewProps
> = ({
  materialPricing,
  pricingError,
  pricingLoading,
  view,
  handleSelectView,
  showAllOptions,
  setShowAllOptions,
  isBuyBoxSheetOpen,
  setIsBuyBoxSheetOpen,
  product,
  countryCode,
  isMultiCountryUser,
  mobileSelectedMaterial,
  setMobileSelectedMaterial,
  handleCloseBuyBoxSheet,
  discontinuedPricingInfo,
}) => {
  const { classes } = useStyles()
  const isInDisplayedMaterials = materialPricing?.some(
    (material) =>
      material.materialNumber === mobileSelectedMaterial?.materialNumber
  )

  const boxRef = useRef<HTMLDivElement | null>(null)

  // maybe break out height and top into sep state vars
  const [selectedMaterialPosition, setSelectedMaterialPosition] = useState(
    {} as SelectedMaterialPosition
  )

  useEffect(() => {
    if (isBuyBoxSheetOpen && boxRef.current) {
      const buyBoxHeight = boxRef?.current?.clientHeight || 0
      const selectedOffsetHeight = selectedMaterialPosition?.height
      const selectedTop = selectedMaterialPosition?.top
      const topScrollTo =
        selectedOffsetHeight -
        window.innerHeight +
        buyBoxHeight +
        SELECTED_OFFSET
      const boxRectTop = boxRef?.current?.getBoundingClientRect().top || 0

      if (selectedTop > boxRectTop) {
        window.scrollTo({
          top: topScrollTo,
          left: 0,
          behavior: 'smooth',
        })
      }
    }
  }, [mobileSelectedMaterial])

  const handleSetMaterialPosition = (height: number, top: number) => {
    setSelectedMaterialPosition({ height, top })
  }
  const isShowPricingArea = !pricingError && !discontinuedPricingInfo

  return (
    <div>
      <div className={classes.mobileSignInBanner}>
        <SignInBanner />
      </div>

      {discontinuedPricingInfo && (
        <div className={classes.pricingError}>
          <DiscontinuedProductAlert
            discontinuedPricingInfo={discontinuedPricingInfo}
          />
        </div>
      )}

      {!discontinuedPricingInfo && !pricingError && product?.paMessage && (
        <ProductPaMessage
          brandKey={product.brandKey}
          productNumber={product.productNumber}
          paMessage={product.paMessage}
          displayType={ProductPaMessageDisplayType.SRP}
          gaPricingOverrides={{
            component: EventActionComponentType.List,
          }}
        />
      )}

      {pricingError && (
        <div className={classes.pricingError}>
          <ProductPriceAvailabilityError
            error={pricingError}
            productNumber={product?.productNumber}
            isCenterJustified={false}
            isMarketplace={product?.isMarketplace}
            sellerName={
              product?.displaySellerName || getSellerName(product?.attributes)
            }
            marketplaceSellerId={product?.marketplaceSellerId ?? ''}
            marketplaceOfferId={product?.marketplaceOfferId ?? ''}
          />
        </div>
      )}

      {isShowPricingArea && (
        <>
          {/* View Toggle */}
          <MobileHeader view={view} handleSelectView={handleSelectView} />

          {/* Product Display */}
          <MobileProductDisplay
            materialPricing={materialPricing}
            pricingLoading={pricingLoading}
            view={view}
            showAllOptions={showAllOptions}
            setShowAllOptions={setShowAllOptions}
            setIsBuyBoxSheetOpen={setIsBuyBoxSheetOpen}
            mobileSelectedMaterial={mobileSelectedMaterial}
            setMobileSelectedMaterial={setMobileSelectedMaterial}
            handleSetMaterialPosition={handleSetMaterialPosition}
          />

          {/* make sure only 1 buybox sheet is loaded in dom */}
          {isInDisplayedMaterials && (
            <NonModalSheet
              open={isBuyBoxSheetOpen}
              onClose={handleCloseBuyBoxSheet}
              containerClass={classes.sheetContainer}
              contentClass={classes.sheetContentContainer}
            >
              <div className={classes.sheetContent} ref={boxRef}>
                <BuyBox
                  isMultiCountryUser={isMultiCountryUser}
                  loading={false}
                  error={pricingError}
                  material={mobileSelectedMaterial}
                  product={product}
                  countryCode={countryCode}
                  showPriceDisclaimer
                  isMobileSrpBuyBox
                />
              </div>
            </NonModalSheet>
          )}
        </>
      )}
    </div>
  )
}

export default ViewProductOptionsMobileView
